// UIButton.jsx
import Link from 'next/link';
import clsx from 'clsx';

import styles from './UIButton.module.css';

export default function UIButton({
  href,
  className = '',
  onClick,
  children,
  variant = 'default',
  rounded,
  ariaLabel = '',
  disabled = false,
  target,
  ...restProps
}) {
  // Only variant classes
  const variantClasses = {
    default:
      'inline-flex items-center justify-center rounded-full bg-black/30 lg:hover:bg-[#00EAFD] lg:hover:text-black transition-all duration-500 backdrop-blur-[10px] text-white text-sm font-semibold leading-[1.5em] px-[14px] py-[6px] disabled:lg:hover:bg-black/30 disabled:lg:hover:text-white',
    primary:
      'px-4 py-2 2xl:text-base text-sm font-medium bg-indigo-600 text-white shadow-xs lg:hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-500 rounded-xl flex items-center justify-center disabled:lg:hover:bg-indigo-600',
    primaryTheme:
      'bg-[#00EAFD] cursor-pointer text-[#323232] px-4 py-2 rounded-lg font-medium lg:hover:bg-[#00d5e7] transition-colors transition-all duration-500 disabled:lg:hover:bg-[#00EAFD]',
    secondaryTheme:
      'cursor-pointer bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium lg:hover:bg-gray-200 transition-colors transition-all duration-500 disabled:lg:hover:bg-gray-100',
    outline:
      'px-4 py-2 cursor-pointer text-sm font-medium bg-white text-gray-900 ring-1 ring-gray-300 lg:hover:bg-gray-50 rounded-xl flex items-center justify-center disabled:lg:hover:bg-white',
    gradient: clsx(
      styles.borderGradient, // CSS Module class
      'relative bg-white/50 border-none max-w-max gap-2 lg:min-w-[182px] sm:min-w-40 min-w-[140px] justify-between lg:hover:shadow-[0_4px_4px_rgba(0,0,0,0.25)] transition-all duration-600 flex-1 flex items-center leading-[1em] rounded-full xl:py-4 px-5 py-3 text-center text-sm font-normal lg:font-semibold lg:text-base text-[#323232] focus-visible:outline-offset-2 focus-visible:outline-lime-500 disabled:lg:hover:shadow-none'
    ),
  };

  const roundedClasses = {
    full: '!rounded-full',
    lg: '!rounded-lg',
    md: '!rounded-md',
    sm: '!rounded-sm',
    none: '!rounded-none',
  };

  // Apply variant class, fallback to default
  const cls = clsx(
    variantClasses[variant] || variantClasses.default,
    rounded && roundedClasses[rounded],
    className
  );

  // Auto rel for target="_blank"
  const autoRel = target === '_blank' ? 'noopener noreferrer' : undefined;

  if (href) {
    return (
      <Link href={href} className={cls} target={target} rel={autoRel} {...restProps}>
        {children}
      </Link>
    );
  }

  return (
    <button
      disabled={disabled}
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={cls}
      {...restProps}
    >
      {children}
    </button>
  );
}
